import React, { useState, useMemo, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, Col, Divider, Form, InfoCircleOutlined, Input, PlusOutlined, Row, Select, Space, Tag, Tooltip } from "src/assets/imports/antdComponents";
import { CHAR_LENGTH, PATTERN } from "src/config/dataConfig";
import { formatMessage, validateLanguageInput } from "../../transformer";
import { FeatureKeysType, ICreateFolderFormProps, TagOption } from "src/pages/Studies/models/interfaces";
import { RootState } from "src/store";
import "./style.css";

const CreateFolderForm: React.FC<ICreateFolderFormProps> = ({ form, existingFolders = [], onTagsChange, initialValues, disabled = false, className = "" }) => {
	const { t } = useTranslation();
	const { supportedLanguages } = useSelector((state: any) => state.studiesSlice);
	const activeFeatures = useSelector((state: RootState) => state.askKuralSlice.activeFeatures);
	const featureName = activeFeatures?.find((ele) => ele.feature_key === FeatureKeysType.EnableFolder)?.feature_name;

	const [customTagForm] = Form.useForm();
	const [customTagName, setCustomTagName] = useState("");
	const [customTagError, setCustomTagError] = useState(false);
	const [customTagOptions, setCustomTagOptions] = useState<TagOption[]>([]);

	const getFilterOptionFlag = useCallback((input: string, option: any) => {
		return option?.label?.toLowerCase().includes(input.toLowerCase());
	}, []);

	const folderNameValidator = useMemo(() => {
		return async (_: any, value: string) => {
			if (value && value.length > CHAR_LENGTH.M) {
				throw new Error(t(`${featureName} Name cannot exceed ${CHAR_LENGTH.M} characters.`));
			}

			if (value) {
				const errorMessage = validateLanguageInput(supportedLanguages, value, PATTERN.STRICT);
				if (errorMessage) {
					throw new Error(`${t(errorMessage)} ${PATTERN.STRICT}`);
				}
			}

			if (value) {
				const isDuplicateName = existingFolders?.some((folder) => folder?.folder_name?.trim().toLowerCase() === value.trim().toLowerCase());
				if (isDuplicateName) {
					throw new Error(t(`A ${featureName} with this name already exists.`));
				}
			}

			return Promise.resolve();
		};
	}, [supportedLanguages, existingFolders]);

	const descriptionValidator = useMemo(() => {
		return async (_: any, value: string) => {
			if (value && value.length > CHAR_LENGTH.L) {
				throw new Error(t(`Description cannot exceed ${CHAR_LENGTH.L} characters.`));
			}

			if (value) {
				const errorMessage = validateLanguageInput(supportedLanguages, value, PATTERN.FLEXIBLE);
				if (errorMessage) {
					throw new Error(`${t(errorMessage)} ${PATTERN.FLEXIBLE}`);
				}
			}

			return Promise.resolve();
		};
	}, [supportedLanguages]);

	const tagValidationCache = useMemo(() => {
		const cache = new Map<string, boolean>();
		return {
			isValid: (value: string): boolean => {
				if (cache.has(value)) {
					return cache.get(value)!;
				}
				const isValid = !validateLanguageInput(supportedLanguages, value, PATTERN.STRICT);
				cache.set(value, isValid);
				return isValid;
			},
		};
	}, [supportedLanguages]);

	const customTagValidator = useCallback(
		async (_: any, value: string) => {
			if (form.getFieldsValue().folder_tags?.length >= 20) {
				setCustomTagError(true);
				return Promise.reject(new Error(t("Maximum of 20 tags allowed.")));
			}

			const validateDuplicates = customTagOptions.some((ele) => ele.label?.trim().toLowerCase() === value?.trim().toLowerCase());
			if (validateDuplicates) {
				setCustomTagError(true);
				return Promise.reject(new Error(t("Tag name already exists.")));
			}

			if (value && value?.length > CHAR_LENGTH.S) {
				setCustomTagError(true);
				return Promise.reject(new Error(`${t("Tag name cannot exceed")} ${CHAR_LENGTH.S} ${t("characters")}`));
			}

			if (value) {
				const errorMessage = validateLanguageInput(supportedLanguages, value, PATTERN.STRICT);
				if (errorMessage) {
					setCustomTagError(true);
					return Promise.reject(new Error(`${t(errorMessage)} ${PATTERN.STRICT}`));
				}
			}

			setCustomTagError(false);
			return Promise.resolve();
		},
		[form, customTagOptions, supportedLanguages],
	);

	const tagRender = useCallback(
		(props: any) => {
			const { label, closable, onClose, value } = props;
			const isValid = tagValidationCache.isValid(value);
			const color = isValid ? "default" : "red";

			const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
				event.preventDefault();
				event.stopPropagation();
			};

			return (
				<Tag color={color} className="form-custom-tag ml-1" onMouseDown={onPreventMouseDown} closable={closable} onClose={onClose}>
					{isValid ? (
						<>{label}</>
					) : (
						<Tooltip title={t("Invalid tag")}>
							<InfoCircleOutlined style={{ marginRight: 4 }} />
							{label}
						</Tooltip>
					)}
				</Tag>
			);
		},
		[tagValidationCache],
	);

	const onCustomTagChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setCustomTagName(e.target.value);
			if (e.target.value.trim()) {
				customTagForm.validateFields(["tagName"]);
			}
		},
		[customTagForm],
	);

	const addCustomItem = useCallback(() => {
		const trimmedValue = customTagName.trim();
		if (!trimmedValue || customTagError) return;

		const newOption: TagOption = {
			label: trimmedValue,
			value: trimmedValue,
		};

		setCustomTagOptions((prev) => [...prev, newOption]);

		// Add to main form field
		const currentTags = form.getFieldValue("folder_tags") || [];
		const updatedTags = [...currentTags, trimmedValue];
		form.setFieldsValue({
			folder_tags: updatedTags,
		});

		// Trigger callback if provided
		onTagsChange?.(updatedTags);

		setCustomTagName("");
		customTagForm.resetFields(["tagName"]);
	}, [customTagName, customTagError, form, onTagsChange, customTagForm]);

	const handleTagsChange = useCallback(
		(tags: string[]) => {
			onTagsChange?.(tags);
		},
		[onTagsChange],
	);

	const handleTagInputKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			e.stopPropagation();
			const trimmedValue = customTagName.trim();
			if (e.key === "Enter" && !customTagError && trimmedValue) {
				addCustomItem();
				e.preventDefault();
			}
		},
		[customTagName, customTagError, addCustomItem],
	);

	const dropdownRender = useCallback(
		(menu: React.ReactElement) => (
			<>
				{menu}
				{!disabled && (
					<>
						<Divider style={{ margin: "8px 0" }} />
						<Form name="addCustomTags" form={customTagForm} autoComplete="off" className="addCustomTags -mb-6">
							<Form.Item
								name="tagName"
								className="space-y-0 flex items-center"
								rules={[
									{
										validator: customTagValidator,
									},
								]}>
								<Space style={{ padding: "0 8px 4px" }}>
									<Input
										placeholder={t("Add new tag")}
										value={customTagName}
										onChange={onCustomTagChange}
										onKeyDown={handleTagInputKeyDown}
										className="custom-tags-input"
										maxLength={CHAR_LENGTH.S}
									/>
									<Button
										className="text-[--primary-color] form-add-custom-tag"
										disabled={customTagError || !customTagName.trim()}
										type="text"
										icon={<PlusOutlined />}
										onClick={addCustomItem}
										size="small">
										{t("Add")}
									</Button>
								</Space>
							</Form.Item>
						</Form>
					</>
				)}
			</>
		),
		[disabled, customTagForm, customTagValidator, customTagName, onCustomTagChange, handleTagInputKeyDown, customTagError, addCustomItem],
	);

	const handleSelectOpenChange = useCallback(() => {
		setCustomTagName("");
		setCustomTagError(false);
		customTagForm.resetFields(["tagName"]);
	}, [customTagForm]);

	const listHeight = useMemo(() => {
		return Math.min(customTagOptions.length * 32 + 100, 300);
	}, [customTagOptions.length]);

	return (
		<Form form={form} layout="vertical" autoComplete="off" className={`antd-form-animation ${className}`} initialValues={initialValues} disabled={disabled}>
			<Row gutter={[8, 8]}>
				<Col span={24}>
					<Form.Item
						name="folder_name"
						label={formatMessage("{featureName} Name", t, featureName)}
						rules={[{ required: true, message: formatMessage("{featureName} name is required", t, featureName) }, { validator: folderNameValidator }]}
						className="mb-4">
						<Input placeholder={formatMessage("Enter {featureName} name", t, featureName)} disabled={disabled} />
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item name="folder_description" label={t("Description")} rules={[{ validator: descriptionValidator }]} className="mb-4">
						<Input.TextArea placeholder={t("Enter description")} autoSize={{ minRows: 4, maxRows: 4 }} disabled={disabled} />
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item name="folder_tags" label={t("Tags")} className="mb-0">
						<Select
							mode="multiple"
							showSearch
							placeholder={t("Select or add tags")}
							filterOption={getFilterOptionFlag}
							tagRender={tagRender}
							options={customTagOptions}
							listHeight={listHeight}
							popupClassName="custom-tags-dropdown"
							maxCount={20}
							disabled={disabled}
							onChange={handleTagsChange}
							onOpenChange={handleSelectOpenChange}
							dropdownRender={dropdownRender}
						/>
					</Form.Item>
				</Col>
			</Row>
		</Form>
	);
};

export default memo(CreateFolderForm);
